/**
 * Acceso al access token de Auth0 desde código que NO es un componente React.
 *
 * Los clientes de CubeJS se construyen una sola vez y viven mientras dure la
 * pestaña. Si el token se hornea en la construcción, envejece hasta vencer sin
 * que nada lo renueve. Registrando una referencia a `getAccessTokenSilently`
 * del SDK de Auth0 —que mantiene el token cacheado y lo renueva solo cuando
 * corresponde— el cliente puede pedirlo en cada request.
 *
 * Patrón oficial de @auth0/auth0-react para acceder al token fuera de React.
 * El registro lo hace `<AccessTokenRegistrar />`, que solo registra el getter
 * cuando hay sesión — ver el comentario del tope de espera, más abajo.
 */

/**
 * Tope de espera propio para obtener el token.
 *
 * `getAccessTokenSilently` no falla rápido cuando no puede resolver: dispara un
 * silent-auth por iframe contra Auth0 y, si nadie contesta, espera los 60s de
 * su `DEFAULT_AUTHORIZE_TIMEOUT_IN_SECONDS`. Ninguna consulta de la app puede
 * quedar colgada de la latencia de Auth0. Al vencer se devuelve `undefined` y
 * la request sale sin header, que es el mismo camino degradado de siempre.
 */
export const TOKEN_REQUEST_TIMEOUT_MS = 3000;

let accessTokenGetter = null;

/**
 * Registra la función que provee el access token (típicamente
 * `getAccessTokenSilently` de `useAuth0()`). Pasar `null` la desregistra.
 * @param {(() => Promise<string>)|null} getter
 */
export function registerAccessTokenGetter(getter) {
  accessTokenGetter = getter;
}

/**
 * Devuelve el access token vigente, o `undefined` si todavía no hay sesión ni
 * getter registrado, si el getter falla, o si tarda más de
 * `TOKEN_REQUEST_TIMEOUT_MS`. Nunca lanza y nunca demora más que ese tope: el
 * caller degrada sin romper y sin colgarse.
 * @returns {Promise<string|undefined>}
 */
export async function getAccessToken() {
  if (!accessTokenGetter) return undefined;

  let timeoutId;
  try {
    const seAgotoLaEspera = new Promise((resolve) => {
      timeoutId = setTimeout(() => resolve(undefined), TOKEN_REQUEST_TIMEOUT_MS);
    });

    return await Promise.race([accessTokenGetter(), seAgotoLaEspera]);
  } catch {
    return undefined;
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Valor del header `Authorization` para CubeJS, con el token vigente.
 *
 * Se pasa como función a `cubejs(fn, { transport })`: el cliente la invoca en
 * `updateTransportAuthorization()` antes de cada request, así que el token se
 * resuelve en el momento de consultar y no en el de construir el cliente.
 *
 * Sin token devuelve cadena vacía, **no** `undefined`: el `HttpTransport` de
 * CubeJS arma el header como `Authorization: transport.authorization` sin
 * comprobar nada, así que un `undefined` viaja por la red como la cadena
 * literal `"undefined"` — verificado contra la librería real. La cadena vacía
 * deja el header sin credencial, que es lo que corresponde cuando todavía no
 * hay sesión, el getter falló, o se agotó `TOKEN_REQUEST_TIMEOUT_MS`.
 * @returns {Promise<string>}
 */
export async function getCubejsAuthorization() {
  const token = await getAccessToken();
  return token ? `Bearer ${token}` : "";
}
